import type {
  AudienceCubeCell,
  AudienceFilter,
  BreakdownDimension,
  EmailKind,
  EmailSendStatus,
  ExclusionReason,
  FaceSegment,
  Funnel,
  SequenceProgress,
  Recipient,
} from './types';
import { PLATFORM_LABELS, type Platform } from '../lib/platform';
import { placeName } from '../search/params';

/**
 * Pure helpers for the Email page: labels, the segment maths over the
 * audience cube, and rates. No React, so it is all unit-tested.
 */

export const KIND_LABELS: Record<EmailKind, string> = {
  personal: 'Personal',
  management: 'Management',
  business: 'Business',
};

export const KIND_HINTS: Record<EmailKind, string> = {
  personal: 'Gmail, iCloud and other free inboxes. Usually the creator reads it.',
  management: 'A manager or agency: mgmt@, bookings@, or a domain several creators share.',
  business: "The creator's own domain or a company inbox.",
};

export const FACE_LABELS: Record<FaceSegment, string> = {
  face: 'On camera',
  faceless: 'Faceless',
  unknown: 'Not checked',
};

export const FACE_HINTS: Record<FaceSegment, string> = {
  face: 'A person fronts the account.',
  faceless: 'Clips, edits and reposts — nobody on camera.',
  unknown: "The on-camera check hasn't run on these creators.",
};

export const ACCOUNT_TYPE_LABELS: Record<string, string> = {
  personal: 'Personal',
  editor: 'Editor',
  clipper: 'Clipper',
  fan_page: 'Fan page',
  news_gossip: 'News / gossip',
  brand_official: 'Brand',
  unknown: 'Unclear',
  unclassified: 'Not classified',
};

export const SIZE_LABELS: Record<string, string> = {
  under_10k: 'Under 10K',
  '10k_50k': '10K – 50K',
  '50k_250k': '50K – 250K',
  over_250k: 'Over 250K',
  unknown: 'Unknown size',
};
const SIZE_ORDER = ['under_10k', '10k_50k', '50k_250k', 'over_250k', 'unknown'];

export const EXCLUSION_LABELS: Record<ExclusionReason, string> = {
  alreadyEmailed: 'Already in an email campaign',
  inConversation: 'Messaged or logged by an outreacher',
  inQueue: 'In the DM queue, not messaged yet',
  joined: 'Already joined',
  doNotEmail: 'Bounced or unsubscribed',
  suppressed: 'Suppressed',
  unsendable: 'Not a real address',
};

export const DIMENSION_LABELS: Record<BreakdownDimension, string> = {
  kind: 'Email type',
  face: 'On camera',
  platform: 'Platform',
  accountType: 'Account type',
  size: 'Followers',
};

export function segmentLabel(dimension: BreakdownDimension, key: string): string {
  switch (dimension) {
    case 'kind': return KIND_LABELS[key as EmailKind] ?? key;
    case 'face': return FACE_LABELS[key as FaceSegment] ?? key;
    case 'platform': return PLATFORM_LABELS[key as Platform] ?? key;
    case 'accountType': return ACCOUNT_TYPE_LABELS[key] ?? key;
    case 'size': return SIZE_LABELS[key] ?? key;
  }
}

/** Follower bands read in size order; everything else biggest group first. */
export function sortBreakdown<T extends { key: string; total: number }>(dimension: BreakdownDimension, rows: T[]): T[] {
  if (dimension === 'size') return [...rows].sort((a, b) => SIZE_ORDER.indexOf(a.key) - SIZE_ORDER.indexOf(b.key));
  return [...rows].sort((a, b) => b.total - a.total);
}

// ── Audience cube ───────────────────────────────────────────────────────────

type SegmentSelection = Pick<AudienceFilter, 'kinds' | 'faces' | 'platforms' | 'accountTypes'>;
type CubeDimension = 'kinds' | 'faces' | 'platforms' | 'accountTypes';

const cellValue = (cell: AudienceCubeCell, dim: CubeDimension): string =>
  dim === 'kinds' ? cell.kind
  : dim === 'faces' ? cell.face
  : dim === 'platforms' ? cell.platform
  : cell.accountType ?? 'unclassified';

function matches(cell: AudienceCubeCell, sel: SegmentSelection, except?: CubeDimension): boolean {
  for (const dim of ['kinds', 'faces', 'platforms', 'accountTypes'] as const) {
    if (dim === except) continue;
    const chosen = sel[dim] as readonly string[] | undefined;
    if (chosen?.length && !chosen.includes(cellValue(cell, dim))) return false;
  }
  return true;
}

/** How many addresses the current segment selection takes. */
export function countMatching(cube: AudienceCubeCell[], sel: SegmentSelection): number {
  return cube.reduce((n, c) => (matches(c, sel) ? n + c.count : n), 0);
}

/**
 * Counts for one dimension's options, given what's chosen on the OTHER
 * dimensions — so a tile shows how many you'd get by picking it, and picking
 * one value doesn't zero out its siblings.
 */
export function facetCounts(cube: AudienceCubeCell[], sel: SegmentSelection, dim: CubeDimension): Map<string, number> {
  const out = new Map<string, number>();
  for (const cell of cube) {
    if (!matches(cell, sel, dim)) continue;
    const v = cellValue(cell, dim);
    out.set(v, (out.get(v) ?? 0) + cell.count);
  }
  return out;
}

export function toggle<T extends string>(list: readonly T[] | undefined, value: T): T[] {
  const set = new Set(list ?? []);
  if (set.has(value)) set.delete(value);
  else set.add(value);
  return [...set];
}

/**
 * Deal `total` creators as evenly as possible across `parts` campaigns. The
 * first campaigns take the remainder, one each, so no two differ by more than one.
 */
export function splitCounts(total: number, parts: number): number[] {
  if (parts < 1) return [];
  const base = Math.floor(total / parts);
  const extra = total % parts;
  return Array.from({ length: parts }, (_, i) => base + (i < extra ? 1 : 0));
}

// ── Sequence ────────────────────────────────────────────────────────────────

export interface SequenceStage {
  /** A recipients `stage` filter value. */
  key: string;
  label: string;
  count: number;
}

/**
 * The sequence as stages, in order: not sent yet, each email, finished. Shows
 * every email in the sequence when its length is known, even at zero, so an
 * empty "Email 3" reads as "not reached yet" rather than missing.
 */
export function sequenceStages(p: SequenceProgress, stepCount?: number | null): SequenceStage[] {
  const emails = Math.max(stepCount ?? 0, p.steps.length);
  return [
    { key: 'not_sent', label: 'Not sent yet', count: p.notSent },
    ...Array.from({ length: emails }, (_, i) => ({ key: `step_${i + 1}`, label: `On email ${i + 1}`, count: p.steps[i] ?? 0 })),
    { key: 'finished', label: 'Finished', count: p.finished },
  ];
}

/** Where one creator is: "Email 2 of 4", "Replied after email 1", "Finished: all 4 sent"… */
export function recipientStep(
  r: Pick<Recipient, 'lastStep' | 'finishedAt' | 'repliedAt' | 'bouncedAt' | 'unsubscribedAt' | 'pushedAt'>,
  stepCount?: number | null,
): string {
  const n = r.lastStep ?? 0;
  const of = stepCount ? ` of ${stepCount}` : '';
  if (r.repliedAt) return n > 0 ? `Replied after email ${n}` : 'Replied';
  if (r.bouncedAt) return 'Bounced';
  if (r.unsubscribedAt) return 'Unsubscribed';
  if (r.finishedAt) return n > 0 ? `Finished: all ${n} sent` : 'Finished';
  if (n > 0) return `Email ${n}${of}`;
  return r.pushedAt ? 'Not sent yet' : '—';
}

// ── Funnel ──────────────────────────────────────────────────────────────────

/** A share as "12.5%", or an em dash when there is nothing to divide by. */
export function rate(part: number, whole: number): string {
  if (whole <= 0) return '—';
  const pct = (part / whole) * 100;
  if (pct === 0) return '0%';
  if (pct < 1) return '<1%';
  return `${pct >= 10 ? Math.round(pct) : Math.round(pct * 10) / 10}%`;
}

export interface FunnelStep {
  key: 'added' | 'sent' | 'opened' | 'clicked' | 'replied' | 'joined' | 'posted';
  label: string;
  count: number;
  /** Share of the emails actually sent — the denominator every step shares. */
  ofSent: string;
  hint: string;
}

/**
 * The steps in order. Rates are all "of sent" rather than step-over-step:
 * opens are unreliable (image blocking, privacy proxies), so a reply rate
 * computed over opens moves for reasons that have nothing to do with the copy.
 */
export function funnelSteps(f: Funnel, provider: 'instantly' | 'csv'): FunnelStep[] {
  const sent = f.sent;
  const step = (key: FunnelStep['key'], label: string, count: number, hint: string): FunnelStep => ({
    key, label, count, hint, ofSent: key === 'added' || key === 'sent' ? '' : rate(count, sent),
  });
  if (provider === 'csv') {
    return [
      step('added', 'Exported', f.added, 'Addresses in the file'),
      { ...step('joined', 'Joined', f.joined, 'Signed up after the export'), ofSent: rate(f.joined, f.added) },
      { ...step('posted', 'Posted', f.posted ?? 0, 'Posted after the export'), ofSent: rate(f.posted ?? 0, f.added) },
    ];
  }
  return [
    step('added', 'In campaign', f.added, 'Accepted by Instantly'),
    step('sent', 'Sent', sent, 'Emailed at least once'),
    step('opened', 'Opened', f.opened, 'At least once'),
    step('clicked', 'Clicked', f.clicked, 'A link in the email'),
    step('replied', 'Replied', f.replied, 'Wrote back'),
    step('joined', 'Joined', f.joined, 'Signed up after the email'),
    step('posted', 'Posted', f.posted ?? 0, 'Posted after the email'),
  ];
}

/** Anything in the funnel that needs a person to look at it. */
export function funnelProblems(f: Funnel): string[] {
  const out: string[] = [];
  if (f.failed > 0) out.push(`${f.failed.toLocaleString()} couldn't be pushed and can be retried`);
  if (f.queued > 0) out.push(`${f.queued.toLocaleString()} still waiting to be pushed`);
  // Above ~2% bounces, mailbox providers start treating the sender as careless.
  if (f.sent >= 50 && f.bounced / f.sent > 0.02) {
    out.push(`Bounce rate is ${rate(f.bounced, f.sent)} — above 2% hurts deliverability. Verify the list before sending more`);
  }
  return out;
}

export const STATUS_LABELS: Record<EmailSendStatus, string> = {
  queued: 'Waiting',
  pushed: 'In campaign',
  exported: 'Exported',
  sent: 'Sent',
  opened: 'Opened',
  clicked: 'Clicked',
  replied: 'Replied',
  bounced: 'Bounced',
  unsubscribed: 'Unsubscribed',
  skipped: 'Not accepted',
  failed: 'Push failed',
};

export const STAGE_FILTERS: { value: string; label: string }[] = [
  { value: 'sent', label: 'Sent' },
  { value: 'not_sent', label: 'Not sent yet' },
  { value: 'opened', label: 'Opened' },
  { value: 'no_open', label: 'Sent, no open' },
  { value: 'clicked', label: 'Clicked' },
  { value: 'replied', label: 'Replied' },
  { value: 'interested', label: 'Interested' },
  { value: 'joined', label: 'Joined' },
  { value: 'posted', label: 'Posted' },
  { value: 'finished', label: 'Sequence finished' },
  { value: 'bounced', label: 'Bounced' },
  { value: 'unsubscribed', label: 'Unsubscribed' },
  { value: 'skipped', label: 'Not accepted' },
  { value: 'failed', label: 'Push failed' },
];

/** The filter as query params: lists comma-joined, defaults and empties left out. */
export function filterQuery(f: AudienceFilter): Record<string, string | number | boolean | undefined> {
  const list = (v: readonly string[] | undefined) => (v && v.length > 0 ? v.join(',') : undefined);
  return {
    search: f.search || undefined,
    folders: list(f.folders),
    purposes: list(f.purposes),
    regions: list(f.regions),
    kinds: list(f.kinds),
    faces: list(f.faces),
    platforms: list(f.platforms),
    accountTypes: list(f.accountTypes),
    minFollowers: f.minFollowers,
    maxFollowers: f.maxFollowers,
    minAvgViews: f.minAvgViews,
    namedOnly: f.namedOnly || undefined,
    excludeCreatorIds: f.excludeCreatorIds?.length ? f.excludeCreatorIds.join(',') : undefined,
    includeEmailed: f.includeEmailed || undefined,
    includeInConversation: f.includeInConversation === false ? false : undefined,
    includeInQueue: f.includeInQueue === false ? false : undefined,
  };
}

/** A sentence describing who a filter selects, for confirm dialogs. */
export function describeFilter(f: AudienceFilter, folderName: (publicId: string) => string): string {
  const parts: string[] = [];
  if (f.kinds?.length) parts.push(`${f.kinds.map((k) => KIND_LABELS[k].toLowerCase()).join(' + ')} emails`);
  if (f.faces?.length) parts.push(f.faces.map((v) => FACE_LABELS[v].toLowerCase()).join(' + '));
  if (f.platforms?.length) parts.push(f.platforms.map((p) => PLATFORM_LABELS[p]).join(' + '));
  if (f.accountTypes?.length) parts.push(f.accountTypes.map((t) => (ACCOUNT_TYPE_LABELS[t] ?? t).toLowerCase()).join(' + '));
  if (f.purposes?.length) parts.push(`${f.purposes.join(' + ')} folders`);
  if (f.folders?.length) parts.push(`in ${f.folders.map(folderName).join(', ')}`);
  if (f.minFollowers != null && f.maxFollowers != null) parts.push(`${compact(f.minFollowers)}–${compact(f.maxFollowers)} followers`);
  else if (f.minFollowers != null) parts.push(`${compact(f.minFollowers)}+ followers`);
  else if (f.maxFollowers != null) parts.push(`up to ${compact(f.maxFollowers)} followers`);
  if (f.minAvgViews != null) parts.push(`${compact(f.minAvgViews)}+ average views`);
  if (f.regions?.length) parts.push(`in ${f.regions.map(placeName).join(', ')}`);
  if (f.namedOnly) parts.push('with a real first name');
  if (f.search) parts.push(`matching "${f.search}"`);
  if (f.excludeCreatorIds?.length) parts.push(`${f.excludeCreatorIds.length.toLocaleString()} taken out by hand`);
  return parts.length ? parts.join(' · ') : 'Everyone you can email';
}

export function compact(n: number | null | undefined): string {
  if (n == null) return '—';
  if (n >= 1_000_000) return `${Math.round(n / 100_000) / 10}M`;
  if (n >= 1_000) return `${Math.round(n / 100) / 10}K`;
  return String(Math.round(n));
}
